import React, { useEffect, useState, useRef } from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import moment from 'moment'
import { useAccount } from '../QueryAccount'

import {
  GET_FAVOURITES,
  GET_DOMAINS_SUBGRAPH,
  GET_REGISTRATIONS_SUBGRAPH,
  GET_ERRORS
} from '../../graphql/queries'
import { decryptName, checkIsDecrypted } from '../../api/labels'

import mq from 'mediaQuery'

import AddressContainer from '../Basic/MainContainer'
import { Title as DefaultTitle } from '../Typography/Basic'
import DefaultEtherScanLink from '../Links/EtherScanLink'
import { getEtherScanAddr, normaliseOrMark } from '../../utils/utils'
import { calculateIsExpiredSoon } from '../../utils/dates'
import DomainList from './DomainList'
import RenewAll from './RenewAll'
import Sorting from './Sorting'
import Filtering from './Filtering'
import Loader from '../Loader'
import Banner from '../Banner'
import Checkbox from '../Forms/Checkbox'
import Pager from './Pager'
import AddReverseRecord from '../AddReverseRecord'
import { InvalidCharacterError } from '../Error/Errors'

import warning from '../../assets/yellowwarning.svg'
import close from '../../assets/close.svg'
import { useBlock } from '../hooks'
import { globalErrorReactive } from '../../apollo/reactiveVars'
import { gql } from '@apollo/client'

const DEFAULT_RESULTS_PER_PAGE = 25

const TopBar = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 60px;
  font-size: 32px;
  color: #2c2d3f;
  font-weight: 600;
  flex-direction: column;

  ${mq.medium`
    flex-direction: row
  `}
`

const Title = styled(DefaultTitle)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
`

const EtherScanLink = styled(DefaultEtherScanLink)`
  min-width: 165px;
  border: 1px solid #ff5b00;
  border-radius: 75px;
  font-size: 14px;
  color: #ff5b00;
  padding: 13px 37px;
  text-align: center;
  margin: 0;
`

const Close = styled('img')`
  position: absolute;
  right: 20px;
  top: 20px;
  &:hover {
    cursor: pointer;
  }
`

const Controls = styled('div')`
  margin-top: 50px;
`

const Actions = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  border-radius: 0 12px 0 0;
  padding: 30px 40px 0 40px;
`

const SelectAll = styled('div')`
  grid-area: selectall;
  justify-content: flex-end;
  padding-right: 40px;
  display: none;

  ${mq.large`
    display: flex;
    padding-right: 10px;
  `}
`

function filterOutReverse(domains) {
  return domains.filter(domain => domain.parent)
}

function normaliseAddress(address) {
  return address.toLowerCase()
}

function decryptNames(domains) {
  return domains.map(d => {
    const name = decryptName(d.domain.name)
    return {
      ...d,
      domain: {
        ...d.domain,
        name: name,
        labelName: checkIsDecrypted(name[0]) ? name.split('.')[0] : null
      }
    }
  })
}

function useDomains({
  resultsPerPage,
  domainType,
  address,
  sort,
  page,
  expiryDate
}) {
  const skip = (page - 1) * resultsPerPage

  const registrationsQuery = useQuery(GET_REGISTRATIONS_SUBGRAPH, {
    variables: {
      id: address,
      first: resultsPerPage,
      skip,
      orderBy: sort.type,
      orderDirection: sort.direction,
      expiryDate
    },
    skip: domainType !== 'registrant',
    fetchPolicy: 'no-cache'
  })

  const controllersQuery = useQuery(GET_DOMAINS_SUBGRAPH, {
    variables: {
      id: address,
      first: resultsPerPage,
      skip
    },
    skip: domainType !== 'controller',
    fetchPolicy: 'no-cache'
  })

  if (domainType === 'registrant') {
    return registrationsQuery
  } else if (domainType === 'controller') {
    return controllersQuery
  } else {
    throw new Error('Unrecognised domainType')
  }
}

const RESET_STATE_QUERY = gql`
  query resetStateQuery @client {
    networkId
    isENSReady
  }
`
export const useResetState = (
  setYears,
  setCheckedBoxes,
  setSelectAll,
  networkId,
  address,
  globalErrorReactive
) => {
  useEffect(() => {
    setYears(1)
    setCheckedBoxes({})
    setSelectAll(null)
    globalErrorReactive({
      ...globalErrorReactive(),
      invalidCharacter: null
    })
  }, [networkId, address])
}

export default function Address({
  url,
  address,
  showOriginBanner,
  domainType = 'registrant'
}) {
  const {
    data: { networkId, isENSReady }
  } = useQuery(RESET_STATE_QUERY)

  const normalisedAddress = normaliseAddress(address)
  const { search } = useLocation()
  const account = useAccount()
  const pageQuery = new URLSearchParams(search).get('page')
  const page = pageQuery ? parseInt(pageQuery) : 1

  const { block } = useBlock()

  let [resultsPerPage, setResultsPerPage] = useState(DEFAULT_RESULTS_PER_PAGE)
  let { t } = useTranslation()
  let [showOriginBannerFlag, setShowOriginBannerFlag] = useState(true)
  let [etherScanAddr, setEtherScanAddr] = useState(null)
  let [activeSort, setActiveSort] = useState({
    type: 'expiryDate',
    direction: 'asc'
  })
  let [checkedBoxes, setCheckedBoxes] = useState({})
  let [years, setYears] = useState(1)
  const [selectAll, setSelectAll] = useState(false)

  useResetState(
    setYears,
    setCheckedBoxes,
    setSelectAll,
    networkId,
    address,
    globalErrorReactive
  )

  // TODO
  // if so, the currentDate varies, so useDomains will run evertime
  // let currentDate

  // if (process.env.REACT_APP_STAGE === 'local') {
  //   if (block) {
  //     currentDate = moment(block.timestamp * 1000)
  //   }
  // } else {
  //   currentDate = moment()
  // }

  // let expiryDate

  // if (currentDate.current) {
  //   expiryDate = currentDate.current.subtract(90, 'days').unix()
  // }

  const expiryDate = useRef(
    moment()
      .subtract(90, 'days')
      .unix()
  )

  const { loading, data, error, refetch } = useDomains({
    resultsPerPage,
    domainType,
    address: normalisedAddress,
    sort: activeSort,
    page,
    expiryDate: expiryDate.current
  })

  const {
    data: { globalError }
  } = useQuery(GET_ERRORS)

  const { data: { favourites } = [] } = useQuery(GET_FAVOURITES)

  useEffect(() => {
    if (isENSReady) {
      getEtherScanAddr().then(setEtherScanAddr)
    }
  }, [isENSReady])

  if (error) {
    console.log(error)
    return <>Error getting domains. {JSON.stringify(error)}</>
  }

  if (loading) {
    return <Loader withWrap large />
  }

  let normalisedDomains = []

  if (domainType === 'registrant' && data.account) {
    normalisedDomains = [...data.account.registrations]
  } else if (domainType === 'controller' && data.account) {
    normalisedDomains = [
      ...filterOutReverse(data.account.domains).map(domain => ({ domain }))
    ]
  }

  let decryptedDomains = normaliseOrMark(
    decryptNames(normalisedDomains),
    'labelName',
    true
  )

  if (globalError.invalidCharacter || !decryptedDomains) {
    return <InvalidCharacterError message={globalError.invalidCharacter} />
  }
  // let sortedDomains = decryptedDomains.sort(getSortFunc(activeSort))
  let domains = decryptedDomains

  const selectedNames = Object.entries(checkedBoxes)
    .filter(([key, value]) => value)
    .map(([key]) => key)

  const allNames = domains
    .filter(d => d.domain.labelName)
    .map(d => d.domain.name)

  const selectAllNames = () => {
    const obj = allNames.reduce((acc, name) => {
      acc[name] = true
      return acc
    }, {})

    setCheckedBoxes(obj)
  }

  const hasNamesExpiringSoon = !!domains.find(domain =>
    calculateIsExpiredSoon(domain.expiryDate)
  )

  const toggleCheckAll = () => {
    if (!selectAll) {
      selectAllNames()
    } else {
      setCheckedBoxes({})
    }

    setSelectAll(selectAll => !selectAll)
  }

  return (
    <>
      {showOriginBanner && showOriginBannerFlag && (
        <Banner>
          <Close onClick={() => setShowOriginBannerFlag(false)} src={close} />
          {t('address.transactionBanner')}
        </Banner>
      )}

      {hasNamesExpiringSoon && (
        <Banner>
          <h3>
            <img alt="exclamation mark" src={warning} />
            &nbsp; {t('address.namesExpiringSoonBanner.title')}
            <p>
              <Trans i18nKey="address.namesExpiringSoonBanner.text">
                One or more names are expiring soon, renew them all in one
                transaction by selecting multiple names and click "Renew"
              </Trans>
            </p>
          </h3>
        </Banner>
      )}

      <AddressContainer>
        <TopBar>
          <Title>{address}</Title>
          {etherScanAddr && (
            <EtherScanLink address={address}>
              {t('address.etherscanButton')}
            </EtherScanLink>
          )}
        </TopBar>

        <AddReverseRecord account={account} currentAddress={address} />

        <Controls>
          <Filtering
            activeFilter={domainType}
            setActiveSort={setActiveSort}
            url={url}
          />

          <Actions>
            <Sorting
              activeSort={activeSort}
              setActiveSort={setActiveSort}
              activeFilter={domainType}
            />

            <div style={{ display: 'flex', alignItems: 'center' }}>
              {domainType === 'registrant' && (
                <>
                  <SelectAll>
                    <Checkbox
                      testid="checkbox-renewall"
                      type="double"
                      checked={selectAll}
                      onClick={toggleCheckAll}
                    />
                  </SelectAll>
                </>
              )}

              {domainType === 'registrant' && (
                <RenewAll
                  years={years}
                  setYears={setYears}
                  activeFilter={domainType}
                  selectedNames={selectedNames}
                  setCheckedBoxes={setCheckedBoxes}
                  setSelectAll={setSelectAll}
                  allNames={allNames}
                  address={address}
                  data={data}
                  refetch={refetch}
                  getterString="account.registrations"
                />
              )}
            </div>
          </Actions>
        </Controls>

        <DomainList
          setSelectAll={setSelectAll}
          address={address}
          domains={domains}
          favourites={normaliseOrMark(favourites, 'labelName')}
          activeSort={activeSort}
          activeFilter={domainType}
          checkedBoxes={checkedBoxes}
          setCheckedBoxes={setCheckedBoxes}
          showBlockies={false}
        />

        <Pager
          variables={{ id: address, expiryDate: expiryDate.current }}
          currentPage={page}
          resultsPerPage={resultsPerPage}
          setResultsPerPage={setResultsPerPage}
          pageLink={`/address/${address}/${domainType}`}
          query={
            domainType === 'registrant'
              ? GET_REGISTRATIONS_SUBGRAPH
              : GET_DOMAINS_SUBGRAPH
          }
        />
      </AddressContainer>
    </>
  )
}
